import { inject, Injectable } from '@angular/core';
import {
  EMPTY,
  exhaustMap,
  filter,
  map,
  of,
  OperatorFunction,
  switchMap,
} from 'rxjs';
import { Option } from '@sapphire/result';
import { Action, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import {
  ImpersonationActions,
  selectHasImpersonatedBuyer,
  selectImpersonatedBuyer,
  selectUser,
} from '@ps/auth-data-access';
import { markCached } from '@ps/utils';
import { ToastService } from '@ps/core-overlay';
import { TEMPLATE_MESSAGES } from '@ps/survey-utils';

import { GetTemplatesParams } from '../api/template.api';
import { TemplateService } from '../services/template.service';
import { TEMPLATE_CACHE_TTL_MS } from '../utils/template-cache.constants';
import { TemplateActions, TemplateApiActions } from './template.actions';
import { selectTemplatesCachedAt } from './template.selectors';

@Injectable()
export class TemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _templateService = inject(TemplateService);
  private readonly _toast = inject(ToastService);

  public readonly loadTemplates$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.loadTemplates),
      withCacheGate(
        this._store.select(selectTemplatesCachedAt),
        TEMPLATE_CACHE_TTL_MS,
      ),
      switchMap(({ hasImpersonatedBuyer, companyId }) =>
        this._templateService
          .getTemplates({ hasImpersonatedBuyer, companyId })
          .pipe(
            map((templates) =>
              TemplateApiActions.loadTemplatesSuccess({
                templates,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError((err) =>
              of(
                TemplateApiActions.loadTemplatesFailure({
                  error: err.message || 'Failed to load templates',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  /**
   * Only reloads when the operator has impersonated a buyer — matches
   * `reloadOnImpersonationContextSettled$` in `@ps/survey-data-access`.
   * The reducer resets to the initial list fields on the same action,
   * so this dispatch always passes the cache gate above.
   */
  public readonly reloadOnImpersonationContextSettled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ImpersonationActions.contextSettled),
      filter(({ hasImpersonatedBuyer }) => hasImpersonatedBuyer),
      map(({ companyId }) =>
        TemplateActions.loadTemplates({
          hasImpersonatedBuyer: true,
          companyId,
        }),
      ),
    ),
  );

  // Uncached: fires only on drawer open, so a gate would cost a second
  // `cachedAt` field and its invalidation to save one request.
  public readonly loadSaveTargetFolders$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.loadSaveTargetFolders),
      switchMap(({ hasImpersonatedBuyer, companyId }) =>
        this._templateService
          .getTemplates({
            hasImpersonatedBuyer,
            companyId,
            forTemplateDrawer: true,
          })
          .pipe(
            map((folders) =>
              TemplateApiActions.loadSaveTargetFoldersSuccess({ folders }),
            ),
            this.psCatchError(
              () => of(TemplateApiActions.loadSaveTargetFoldersFailure()),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly createTemplateFolder$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.createTemplateFolder),
      concatLatestFrom(() => this._store.select(selectHasImpersonatedBuyer)),
      switchMap(([{ name, parentFolderId }, hasImpersonatedBuyer]) =>
        this._templateService
          .createTemplateFolder(name, hasImpersonatedBuyer, parentFolderId)
          .pipe(
            map((folder) =>
              TemplateApiActions.createTemplateFolderSuccess({ folder }),
            ),
            this.psCatchError((err) => {
              this._toast.error(err.message, { keepOpen: false });
              const message = err.message || 'Failed to create template folder';
              return of(
                TemplateApiActions.createTemplateFolderFailure({
                  error: message,
                }),
              );
            }),
          ),
      ),
    ),
  );

  public readonly saveTemplateSnapshot$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.saveTemplateSnapshot),
      exhaustMap(({ input }) =>
        this._templateService
          .saveTemplate(input)
          .pipe(
            this._announce(
              TEMPLATE_MESSAGES.SAVED,
              TemplateApiActions.saveTemplateSuccess,
            ),
          ),
      ),
    ),
  );

  public readonly updateTemplateSnapshot$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.updateTemplateSnapshot),
      exhaustMap(({ templateId, input }) =>
        this._templateService
          .updateTemplate(templateId, input)
          .pipe(
            this._announce(
              TEMPLATE_MESSAGES.UPDATED,
              TemplateApiActions.updateTemplateSuccess,
            ),
          ),
      ),
    ),
  );

  public readonly reloadTemplatesAfterListChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TemplateApiActions.createTemplateFolderSuccess,
        TemplateApiActions.saveTemplateSuccess,
        TemplateApiActions.updateTemplateSuccess,
      ),
      this._withBuyerScope(),
      map((scope) => TemplateActions.loadTemplates(scope)),
    ),
  );

  // Only folder creation: the drawer is still open and resolves the new
  // folder's label out of this tree.
  public readonly reloadSaveTargetFoldersAfterFolderCreated$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(TemplateApiActions.createTemplateFolderSuccess),
        this._withBuyerScope(),
        map((scope) => TemplateActions.loadSaveTargetFolders(scope)),
      ),
  );

  /** The buyer both template lists are scoped to. */
  private _withBuyerScope(): OperatorFunction<Action, GetTemplatesParams> {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectHasImpersonatedBuyer),
          this._store.select(selectImpersonatedBuyer),
          this._store.select(selectUser),
        ]),
        map(([, hasImpersonatedBuyer, impersonatedBuyer, user]) => ({
          hasImpersonatedBuyer,
          companyId:
            impersonatedBuyer?.id ?? Option.from(user).unwrap().companyId,
        })),
      );
  }

  /**
   * Turns a completed write into its success action, toasting either outcome.
   *
   * A failure emits nothing: `notify` already surfaces the message, and no
   * failure action exists because nothing reads a save error.
   */
  private _announce<TAction extends Action>(
    message: string,
    success: () => TAction,
  ): OperatorFunction<void, TAction> {
    return (write$) =>
      write$.pipe(
        map(() => {
          this._toast.success(message, { keepOpen: false });
          return success();
        }),
        this.psCatchError(() => EMPTY, { notify: true }),
      );
  }
}
